import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Mail, Check, AlertCircle } from 'lucide-react';
import { AreaAutocomplete } from './AreaSelector';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * WeeklyAlertsConfig - Component to manage weekly email alert preferences
 */
const WeeklyAlertsConfig = ({ token }) => {
  const [enabled, setEnabled] = useState(false);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Load current status on mount
  useEffect(() => {
    const loadStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/weekly-alerts/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setEnabled(data.enabled);
          setAreas(data.areas || []);
        }
      } catch (error) {
        console.error('Error loading alert status:', error);
      }
      setLoading(false);
    };

    if (token) {
      loadStatus();
    }
  }, [token]);

  const handleSave = async () => {
    if (enabled && areas.length === 0) {
      setMessage({ type: 'error', text: 'Selecione pelo menos uma área de interesse.' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      if (enabled) {
        // Subscribe with areas
        const response = await fetch(`${API_URL}/api/weekly-alerts/subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include',
          body: JSON.stringify(areas)
        });

        if (response.ok) {
          setMessage({ type: 'success', text: 'Preferências salvas! Você receberá alertas às sextas-feiras ao meio-dia.' });
        } else {
          throw new Error('Failed to save');
        }
      } else {
        // Unsubscribe
        const response = await fetch(`${API_URL}/api/weekly-alerts/unsubscribe`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });

        if (response.ok) {
          setMessage({ type: 'success', text: 'Alertas semanais desativados.' });
        } else {
          throw new Error('Failed to unsubscribe');
        }
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      setMessage({ type: 'error', text: 'Erro ao salvar preferências. Tente novamente.' });
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="weekly-alerts-config">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#002FA7] to-[#0040E0] px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 rounded-full p-2">
            <Mail className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Alertas Semanais</h3>
            <p className="text-sm text-white/80">Receba chamadas da sua área por email</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {enabled ? (
              <Bell className="h-5 w-5 text-[#002FA7]" />
            ) : (
              <BellOff className="h-5 w-5 text-gray-400" />
            )}
            <div>
              <div className="font-medium text-gray-900">
                {enabled ? 'Alertas ativados' : 'Alertas desativados'}
              </div>
              <div className="text-sm text-gray-500">
                {enabled 
                  ? 'Você receberá emails às sextas-feiras ao meio-dia' 
                  : 'Ative para receber novidades por email'}
              </div>
            </div>
          </div>
          
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="sr-only peer"
              data-testid="weekly-alerts-toggle"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#002FA7]"></div>
          </label>
        </div>

        {/* Areas selection (only shown when enabled) */}
        {enabled && (
          <div className="border-t border-gray-100 pt-6">
            <AreaAutocomplete
              value={areas}
              onChange={setAreas}
              label="Áreas de Interesse para Alertas"
              maxSelections={5}
              placeholder="Digite o nome da área (ex: sociologia, química...)"
            />
            
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-[#002FA7] flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  <p className="font-medium text-[#002FA7]">Como funciona?</p>
                  <ul className="mt-1 space-y-1 list-disc list-inside text-gray-600">
                    <li>Você receberá um email toda sexta-feira às 12h</li>
                    <li>O email mostrará chamadas publicadas nos últimos 7 dias</li>
                    <li>Apenas chamadas das suas áreas de interesse serão incluídas</li>
                    <li>Se não houver chamadas novas, nenhum email será enviado</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Message */}
        {message && (
          <div className={`p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800' 
              : 'bg-red-50 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <Check className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            {message.text}
          </div>
        )}

        {/* Save button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-[#002FA7] hover:bg-[#002FA7]/90"
          data-testid="save-alerts-btn"
        >
          {saving ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
              Salvando...
            </>
          ) : (
            'Salvar Preferências'
          )}
        </Button>
      </div>
    </div>
  );
};

export default WeeklyAlertsConfig;
